import React from 'react';
import styled, { css } from 'styled-components';
import { m } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  as?: keyof React.JSX.IntrinsicElements | React.ComponentType<any>;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
  whileHover?: any;
  whileTap?: any;
}

const getVariantStyles = (variant: ButtonProps['variant'], isDark: boolean, hasCustomHover?: boolean) => {
  const styles = {
    primary: css`
      background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
      color: white;
      border: 2px solid transparent;
      position: relative;
      z-index: 1;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, ${props => props.theme.colors.primaryDark}, ${props => props.theme.colors.primary});
        opacity: 0;
        transition: opacity ${props => props.theme.transitions.fast};
        border-radius: inherit;
        z-index: -1;
      }

      &:hover:not(:disabled) {
        color: white; /* Ensure white text on hover */
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(10, 93, 235, 0.3);
        
        &::before {
          opacity: 1;
        }
      }

      &:active:not(:disabled) {
        color: white; /* Ensure white text on active */
        transform: translateY(0);
      }
    `,
    secondary: css`
      background-color: ${props => isDark ? props.theme.colors.dark.card : props.theme.colors.light.backgroundSecondary};
      color: ${props => props.theme.colors.primary};
      border: 2px solid transparent;

      &:hover:not(:disabled) {
        background-color: ${props => isDark ? props.theme.colors.dark.cardHover : props.theme.colors.light.cardHover};
        transform: translateY(-2px);
        box-shadow: ${props => props.theme.shadows.md};
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    `,
    outline: css`
      background-color: transparent;
      color: ${props => props.theme.colors.primary};
      border: 2px solid ${props => props.theme.colors.primary};

      ${!hasCustomHover && css`
        &:hover:not(:disabled) {
          background-color: ${props => props.theme.colors.primary};
          color: white;
          transform: translateY(-2px);
          box-shadow: ${props => props.theme.shadows.md};
        }

        &:active:not(:disabled) {
          background-color: ${props => props.theme.colors.primary};
          color: white;
          transform: translateY(0);
        }
      `}
    `,
    ghost: css`
      background-color: transparent;
      color: ${props => isDark ? props.theme.colors.dark.text : props.theme.colors.light.text};
      border: 2px solid transparent;

      &:hover:not(:disabled) {
        background-color: ${props => isDark ? props.theme.colors.dark.cardHover : props.theme.colors.light.cardHover};
        transform: translateY(-2px);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    `,
  };

  return styles[variant || 'primary'];
};

const getSizeStyles = (size: ButtonProps['size']) => {
  const sizes = {
    small: css`
      padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
      font-size: ${props => props.theme.typography.fontSize.sm};
    `,
    medium: css`
      padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
      font-size: ${props => props.theme.typography.fontSize.base};
    `,
    large: css`
      padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[8]};
      font-size: ${props => props.theme.typography.fontSize.lg};
    `,
  };

  return sizes[size || 'medium'];
};

const StyledButton = styled(m.button)<{ 
  $variant: ButtonProps['variant']; 
  $size: ButtonProps['size']; 
  $fullWidth: boolean;
  $isDark: boolean;
  $hasCustomHover?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
  font-family: 'Poppins', ${props => props.theme.typography.fontFamily.sans};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: 0.02em;
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: ${props => props.$hasCustomHover ? 'none' : `all ${props.theme.transitions.fast}`};
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  position: relative;
  overflow: hidden;

  ${props => getVariantStyles(props.$variant, props.$isDark, props.$hasCustomHover)}
  ${props => getSizeStyles(props.$size)}
  ${props => props.$fullWidth && css`width: 100%;`}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}33;
  }
`;

const LoadingSpinner = styled(m.div)`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span<{ position: 'left' | 'right' }>`
  display: inline-flex;
  align-items: center;
  order: ${props => props.position === 'right' ? 1 : -1};
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  children,
  disabled,
  as,
  to,
  href,
  target,
  rel,
  whileHover,
  whileTap,
  ...props
}) => {
  const { isDark } = useTheme();

  // Filter out motion props and other props that shouldn't be passed to DOM
  const { onMouseEnter, onMouseLeave, ...filteredProps } = props;
  
  const buttonProps = {
    ...filteredProps,
    ...(to && { to }),
    ...(href && { href }),
    ...(target && { target }),
    ...(rel && { rel }),
    ...(onMouseEnter && { onMouseEnter }),
    ...(onMouseLeave && { onMouseLeave })
  };

  // Default motion states
  const defaultHover = { scale: disabled || isLoading ? 1 : 1.02 };
  const defaultTap = { scale: disabled || isLoading ? 1 : 0.98 };
  
  // Check if custom hover animation is provided (has more than just scale)
  const hasCustomHover = whileHover && (
    Object.keys(whileHover).length > 1 || 
    !whileHover.scale || 
    whileHover.backgroundColor ||
    whileHover.color
  );

  return (
    <StyledButton
      as={as as any}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $isDark={isDark}
      $hasCustomHover={hasCustomHover}
      disabled={disabled || isLoading}
      {...(whileHover && { whileHover })}
      {...(whileTap && { whileTap })}
      {...buttonProps}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && icon && <IconWrapper position={iconPosition}>{icon}</IconWrapper>}
      {children}
    </StyledButton>
  );
};
